import React, { useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Material } from '@models/material.model';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { Checkbox } from '@components/atoms/Checkbox';
import { FormGroup } from '@components/molecules/form/FormGroup';
import ReactSelect from '@components/atoms/ReactSelect';
import { GroupedSelect } from '@components/atoms/ReactSelect/GroupedSelect';
import {
  ImperialWeightOptions,
  MetricWeightOptions,
  WeightOptions,
} from '@utils/options';
import {
  formatDateForSubmission,
  formatDateTimeForSubmission,
} from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';

// Convert options to ReactSelect format
const convertOptions = (
  options: { id: string; name: string; group?: string }[],
) =>
  options.map((option) => ({
    value: option.id,
    label: option.name,
    group: option.group,
  }));

const weightOptions = convertOptions(WeightOptions);
const metricWeightOptions = convertOptions(MetricWeightOptions);
const imperialWeightOptions = convertOptions(ImperialWeightOptions);

interface CreateEditMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (material: any) => void;
  material?: Material | null;
  isDelegated?: boolean;
}

interface MaterialFormValues {
  name: string;
  supplier: string;
  time: string;
  amount: number;
  costPerUnit: number;
  pricePerUnit: number;
  amountType: string;
  amountSystem: string;
  chargeToClient: boolean;
}

const buildValidationSchema = (t: Record<string, string>) =>
  Yup.object().shape({
    name: Yup.string().min(3, t.nameMinLength).required(t.nameRequired),
    supplier: Yup.string()
      .min(3, t.supplierMinLength)
      .required(t.supplierRequired),
    time: Yup.string().required(t.supplyDeliveryDateRequired),
    amount: Yup.number().min(0, t.amountPositive).required(t.amountRequired),
    costPerUnit: Yup.number()
      .min(0, t.costPerUnitPositive)
      .required(t.costPerUnitRequired)
      .max(99999999, t.costPerUnitPositive),
    pricePerUnit: Yup.number().when('chargeToClient', {
      is: true,
      then: () =>
        Yup.number()
          .max(99999999, t.pricePerUnitPositiveWhenCharging)
          .min(0, t.pricePerUnitRequiredWhenCharging)
          .required(t.pricePerUnitRequiredWhenCharging),
      otherwise: () => Yup.number().min(0, t.pricePerUnitPositive),
    }),
    amountType: Yup.string().required(t.unitRequired),
    amountSystem: Yup.string().required(t.systemRequired),
    chargeToClient: Yup.boolean(),
  });

export const CreateEditMaterialModal: React.FC<
  CreateEditMaterialModalProps
> = ({ isOpen, onClose, onSave, material, isDelegated = false }) => {
  const t = useTranslation('materialModal');
  const validationSchema = useMemo(() => buildValidationSchema(t), [t]);

  const getInitialValues = (): MaterialFormValues => ({
    name: material?.name || '',
    supplier: material?.supplier || '',
    time: formatDateForSubmission(material?.time || null) || '',
    amount: material?.amount || 0,
    costPerUnit: material?.costPerUnit || 0,
    pricePerUnit: material?.pricePerUnit || 0,
    amountType: material?.amountType || '',
    amountSystem: material?.amountSystem || 'imperial',
    chargeToClient: material?.chargeToClient || false,
  });

  const handleSubmit = (values: MaterialFormValues) => {
    const materialData = {
      ...values,
      materialId: material?.materialId || `temp-${Date.now()}-${Math.random()}`,
      time: formatDateTimeForSubmission(values.time || null),
      pricePerUnit: values.chargeToClient ? values.pricePerUnit : null,
    };

    onSave(materialData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize>
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleSubmit: formikSubmit,
      }) => (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          handleConfirm={formikSubmit}
          confirmText={material ? t.saveChanges : t.addMaterial}
          title={material ? t.editMaterial : t.addMaterial}>
          <Form className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormGroup
                label={t.materialNameLabel}
                htmlFor="name"
                error={touched.name && errors.name ? errors.name : undefined}>
                <Input
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue('name', e.target.value)}
                  placeholder={t.materialNamePlaceholder}
                  autoComplete="off"
                  className={
                    touched.name && errors.name ? 'border-red-500' : ''
                  }
                />
              </FormGroup>

              <FormGroup
                label={t.supplierLabel}
                htmlFor="supplier"
                error={
                  touched.supplier && errors.supplier
                    ? errors.supplier
                    : undefined
                }>
                <Input
                  name="supplier"
                  value={values.supplier}
                  onChange={(e) => setFieldValue('supplier', e.target.value)}
                  placeholder={t.materialSupplierPlaceholder}
                  className={
                    touched.supplier && errors.supplier ? 'border-red-500' : ''
                  }
                />
              </FormGroup>
            </div>

            {/* Date */}
            <FormGroup
              label={t.supplyDeliveryDateLabel}
              htmlFor="time"
              error={touched.time && errors.time ? errors.time : undefined}>
              <Input
                type="date"
                name="time"
                value={values.time}
                onChange={(e) => setFieldValue('time', e.target.value)}
                className={`block w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-blue-500 focus:border-blue-500 ${
                  touched.time && errors.time ? 'border-red-500' : ''
                }`}
              />
            </FormGroup>

            {/* System and Unit Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormGroup
                label={t.systemLabel}
                htmlFor="amountSystem"
                error={
                  touched.amountSystem && errors.amountSystem
                    ? errors.amountSystem
                    : undefined
                }>
                <ReactSelect
                  name="amountSystem"
                  options={weightOptions}
                  value={weightOptions.find(
                    (option) => option.value === values.amountSystem,
                  )}
                  onChange={(option: any) => {
                    setFieldValue('amountSystem', option?.value || '');
                    setFieldValue('amountType', ''); // Reset unit when system changes
                  }}
                  placeholder={t.metricImperialPlaceholder}
                  className={
                    touched.amountSystem && errors.amountSystem
                      ? 'border-red-500'
                      : ''
                  }
                />
              </FormGroup>

              <FormGroup
                label={t.unitLabel}
                htmlFor="amountType"
                error={
                  touched.amountType && errors.amountType
                    ? errors.amountType
                    : undefined
                }>
                <ReactSelect
                  name="amountType"
                  options={
                    values.amountSystem === 'metric'
                      ? metricWeightOptions
                      : imperialWeightOptions
                  }
                  value={(values.amountSystem === 'metric'
                    ? metricWeightOptions
                    : imperialWeightOptions
                  ).find((option) => option.value === values.amountType)}
                  onChange={(option: any) => {
                    setFieldValue('amountType', option?.value || '');
                  }}
                  placeholder={t.unitLabel}
                  customComponent={GroupedSelect}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  className={
                    touched.amountType && errors.amountType
                      ? 'border-red-500'
                      : ''
                  }
                />
              </FormGroup>
            </div>

            {/* Numeric Fields */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormGroup
                label={t.amountLabel}
                htmlFor="amount"
                error={
                  touched.amount && errors.amount ? errors.amount : undefined
                }>
                <Input
                  name="amount"
                  type="number"
                  value={values.amount.toString()}
                  onChange={(e) =>
                    setFieldValue('amount', Number(e.target.value))
                  }
                  placeholder="120"
                  className={
                    touched.amount && errors.amount ? 'border-red-500' : ''
                  }
                />
              </FormGroup>

              <FormGroup
                label={t.costPerUnitLabel}
                htmlFor="costPerUnit"
                error={
                  touched.costPerUnit && errors.costPerUnit
                    ? errors.costPerUnit
                    : undefined
                }>
                <InputCurrency
                  name="costPerUnit"
                  value={values.costPerUnit}
                  onChange={(value) => setFieldValue('costPerUnit', value || 0)}
                  placeholder="0.00"
                  error={touched.costPerUnit && !!errors.costPerUnit}
                />
              </FormGroup>
            </div>

            {/* Price per Unit (conditional) */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Charge to Client Checkbox */}
              {isDelegated && (
                <FormGroup label="" htmlFor="chargeToClient">
                  <Checkbox
                    name="chargeToClient"
                    checked={values.chargeToClient}
                    onChange={(checked) =>
                      setFieldValue('chargeToClient', checked)
                    }
                    label={t.chargeToClient}
                  />
                </FormGroup>
              )}
              {values.chargeToClient && (
                <FormGroup
                  label={t.pricePerUnitLabel}
                  htmlFor="pricePerUnit"
                  error={
                    touched.pricePerUnit && errors.pricePerUnit
                      ? errors.pricePerUnit
                      : undefined
                  }>
                  <InputCurrency
                    name="pricePerUnit"
                    value={values.pricePerUnit}
                    onChange={(value) =>
                      setFieldValue('pricePerUnit', value || 0)
                    }
                    placeholder="0.00"
                    error={touched.pricePerUnit && !!errors.pricePerUnit}
                  />
                  <div className="flex items-start space-x-2">
                    {values?.pricePerUnit > 0 &&
                      values?.costPerUnit > 0 &&
                      values?.amount > 0 && (
                        <div
                          className={`mt-1 text-sm ${
                            values.pricePerUnit < values.costPerUnit
                              ? 'text-yellow-500'
                              : 'text-green-600'
                          }`}>
                          {t.rawProfit.replace(
                            '{amount}',
                            `$${(
                              values.amount *
                              (values.pricePerUnit - values.costPerUnit)
                            ).toFixed(2)}`,
                          )}
                        </div>
                      )}
                  </div>
                </FormGroup>
              )}
            </div>
          </Form>

        </Modal>
      )}
    </Formik>
  );
};
